import { useState, useEffect } from 'react';
import serviceMethods from '../../service';
import { useAuth } from '../../contexts/auth';
import { useQueryClient } from '@tanstack/react-query';
import DynamicCustomForm from '@Components/DynamicCustomForm';
import { StageDataField } from '@Components/Flows';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { STAGE_CONDITIONS_NOT_MET_MESSAGE } from '../../utils/flowStage';

const CustomForm = ({
  selectedFlowStageId,
  prevResponseId,
  flowInstanceId,
  fishId,
  formDataHydrate,
  customFormId,
}) => {
  const [customForm, setCustomForm] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [stageConditionsNotMet, setStageConditionsNotMet] = useState(false);
  const [isFormView, setIsFormView] = useState(!formDataHydrate);
  const { user } = useAuth();

  const queryClient = useQueryClient();

  const onSubmit = async (formData) => {
    const responseData = {
      customFormId: customFormId,
      answersJson: formData,
      flowStageId: selectedFlowStageId,
      typeformResponseId: prevResponseId,
      userId: user.id,
      flowInstanceId: flowInstanceId ?? null,
      fishId: fishId ?? null,
    };

    const res = await serviceMethods.createCustomFormResponse(responseData);

    if (res) {
      const created = res.ok ? await res.json() : null;
      const conditionsMet = created?.stageConditionsMet !== false;
      setStageConditionsNotMet(!conditionsMet);
      if (conditionsMet) {
        setSubmitSuccess(true);
        setIsFormView(false);
      }
    } else {
      setSubmitSuccess(false);
      setShowMessage(true);
    }

    queryClient.invalidateQueries({ queryKey: ['flowInstance'] });
  };

  const handleGetCustomForm = async (id) => {
    try {
      const customFormRes = await serviceMethods.getCustomFormById(id);
      if (customFormRes.ok) {
        const customForm = await customFormRes.json();

        setCustomForm(customForm);
      }
    } catch {
      console.error('Unable to get custom form');
    }
  };

  useEffect(() => {
    if (customFormId) {
      handleGetCustomForm(customFormId);
    }
    setShowMessage(false);
    setStageConditionsNotMet(false);
  }, [customFormId]);

  return (
    <div className="flex flex-col gap-4">
      {!isFormView && (
        <>
          {formDataHydrate?.answers?.answersJson.map((qa, i) => {
            const label = Object.keys(qa)?.[0];
            const value = qa[label];
            return <StageDataField key={i} label={label} value={value} />;
          })}
        </>
      )}

      {isFormView && customForm && (
        <DynamicCustomForm
          customForm={customForm}
          customFormResponse={formDataHydrate ?? null}
          submitLabel="Submit Review"
          onSubmit={onSubmit}
          onCancel={() => setIsFormView(false)}
        />
      )}

      {stageConditionsNotMet && (
        <p className="text-yellow-600">{STAGE_CONDITIONS_NOT_MET_MESSAGE}</p>
      )}

      {showMessage ? (
        <div>
          <h2 className={submitSuccess ? 'text-yellow-500' : 'text-red-400'}>
            {submitSuccess ? 'Form Submitted' : 'Failed to Submit'}
          </h2>
        </div>
      ) : (
        <></>
      )}

      {!isFormView && (
        <div className="flex w-full justify-end">
          <Button
            onClick={() => {
              setIsFormView(!isFormView);
            }}
          >
            Edit
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomForm;
